import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    makeStyles,
    Grid,
    Button,
} from "@material-ui/core";

import { Controller, useForm } from "react-hook-form";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Error } from "../../common/Error";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addInternalMessage } from "../../../redux/actions";
import { LcmModel } from "../../../domain/LcmModel";
import { authSelector } from "../../../redux/selectors";
import { usersService } from "../../../services";
import { Model } from "@ofqual/portal-core/core/types";
import { Autocomplete } from "@material-ui/lab";
import { InternalMessageModel } from "../../../domain/InternalMessageModel";
import { InternalMessage } from "../types";
import { nameof } from "ts-simple-nameof";

const defaultValues: Partial<InternalMessageModel> = {
    subject: "",
    message: "",
};

const useStyles = makeStyles(() => ({
    actions: {
        padding: 15,
    },
}));

export const InternalMessageDialog: React.FC<{
    onClose: () => void;
    lcmItem: LcmModel;
}> = ({ onClose, lcmItem }) => {

    const { handleSubmit, control, errors } = useForm<InternalMessageModel>({
        defaultValues,
    });

    const classes = useStyles();
    const dispatch = useDispatch();

    const onSubmit = (internalMessage: InternalMessageModel) => {
        dispatch(addInternalMessage(lcmItem.id, internalMessage));
        onClose();
    };

    const [upns, setUpns] = useState([] as Model[]);
    const { token } = useSelector(authSelector);
    useEffect(() => {
        usersService.getUsers({ token }).then((response) => setUpns(response));
    }, [token]);

    return (
        <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle data-test="activity-title">
                Add an internal message
            </DialogTitle>
            <Divider />
            <DialogContent style={{ minHeight: "350px" }}>
                <form>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                name="recipients"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ onChange, ...props }) => (
                                    <Autocomplete
                                        multiple
                                        autoSelect
                                        options={upns}
                                        ChipProps={{ color: "primary" }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                data-test="int-msg-recipient-field"
                                                name="recipients"
                                                variant="outlined"
                                                label="Recipients"
                                            />
                                        )}
                                        getOptionLabel={(option) => option.name}
                                        getOptionSelected={(option, value) => option.id === value}
                                        onChange={(e: any, data: any) => onChange(data)}
                                        {...props}
                                    />
                                )}
                            />
                            {errors.recipients && (
                                <Error data-test="internal-message-recipient-field-error">
                                    This field is required
                                </Error>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                control={control}
                                name="subject"
                                rules={{ required: true }}
                                render={({ onChange, value }) => (
                                    <TextField
                                        onChange={onChange}
                                        value={value}
                                        data-test="internal-message-subject-field"
                                        label="Subject"
                                        required
                                    />
                                )}
                                required
                            />
                            {errors.subject && (
                                <Error data-test="internal-message-subject-field-error">
                                    This field is required
                                </Error>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                control={control}
                                name="message"
                                rules={{ required: true }}
                                render={({ onChange, value }) => (
                                    <MarkdownEditor
                                        onChange={onChange}
                                        value={value}
                                        dataTest="create-int-msg-text-field"
                                    />
                                )}
                            />
                            {errors.message && (
                                <Error data-test="create-internal-message-text-field-error">
                                    This field is required
                                </Error>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                control={control}
                                name={nameof<InternalMessage>(x => x.documents)}
                                as={
                                    <FileInput
                                        multiple={true}
                                        dataTest="create-internal-message-attachment-button"
                                    />
                                }
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    onClick={onClose}
                    color="primary"
                    data-test="close-add-internal-message-dialog"
                >
                    Close
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    color="primary"
                    data-test="add-internal-message-button"
                >
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};
